/**
 * Environment Configuration Utility
 * Handles loading and validating environment variables for the data management tool
 */

/**
 * Validates if a string is a well-formed URL
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid URL, false otherwise
 */
const isValidUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }
  
  try {
    const urlObj = new URL(url);
    // Only allow http and https protocols
    return ['http:', 'https:'].includes(urlObj.protocol);
  } catch (error) {
    return false;
  }
};

/**
 * Loads and validates remote package API configuration from environment variables
 * @returns {Object} - Configuration object with validation results
 */
export const loadRemotePackageApiConfig = () => {
  const endpoint = process.env.REACT_APP_REMOTE_PACKAGE_API_ENDPOINT || 
                   process.env.REMOTE_PACKAGE_API_ENDPOINT || '';
  const token = process.env.REACT_APP_REMOTE_PACKAGE_API_TOKEN || 
                process.env.REMOTE_PACKAGE_API_TOKEN || '';
  
  const result = {
    endpoint: '',
    token: '',
    hasEndpoint: false,
    hasToken: false,
    isEndpointValid: false,
    isConfigured: false,
    errors: []
  };
  
  // Check if endpoint is defined
  if (endpoint) {
    result.hasEndpoint = true;
    result.endpoint = endpoint.trim();
    
    if (!isValidUrl(result.endpoint)) {
      result.errors.push('REMOTE_PACKAGE_API_ENDPOINT is not a valid URL');
    } else {
      result.isEndpointValid = true;
    }
  }
  
  // Check if token is defined
  if (token) {
    result.hasToken = true;
    result.token = token.trim();
  }
  
  // Determine if configuration is complete and valid
  result.isConfigured = result.hasEndpoint && result.hasToken && result.isEndpointValid;

  if (process.env.NODE_ENV === 'development') {
    console.info('[EnvConfig] Remote package environment probe', {
      // CRA/Vite-style public env keys expected at build time
      hasReactAppEndpointVar: Boolean(process.env.REACT_APP_REMOTE_PACKAGE_API_ENDPOINT),
      hasReactAppTokenVar: Boolean(process.env.REACT_APP_REMOTE_PACKAGE_API_TOKEN),
      // Legacy/non-public keys (typically not exposed to browser bundles)
      hasLegacyEndpointVar: Boolean(process.env.REMOTE_PACKAGE_API_ENDPOINT),
      hasLegacyTokenVar: Boolean(process.env.REMOTE_PACKAGE_API_TOKEN),
      // Sanitized resolved state used by the app
      resolved: {
        hasEndpoint: result.hasEndpoint,
        hasToken: result.hasToken,
        isEndpointValid: result.isEndpointValid,
        isConfigured: result.isConfigured,
        endpointPreview: result.endpoint ? `${result.endpoint.slice(0, 40)}...` : '',
        tokenLength: result.token ? result.token.length : 0,
        errors: result.errors
      }
    });
  }
  
  return result;
};

/**
 * Loads and validates Google Drive configuration from environment variables
 * @returns {Object} - Configuration object with validation results
 */
export const loadGoogleDriveConfig = () => {
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || process.env.GOOGLE_CLIENT_ID || '';

  // Debug: Log all REACT_APP_ keys to help diagnose environment variable issues
  // This helps identify if *any* variables are being loaded or if the file is completely ignored.
  // We filter to minimal keys to avoid leaking secrets in logs, but enough to prove .env is read.
  const envKeys = Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'));
  console.log('[EnvConfig] Available REACT_APP_ keys:', envKeys);
  console.log('[EnvConfig] NODE_ENV:', process.env.NODE_ENV);

  if (envKeys.length === 0) {
    console.warn('[EnvConfig] No REACT_APP_ keys found in process.env. This usually means .env file is not picked up by the build tool or dev server.');
    console.warn('[EnvConfig] NOTE: React environment variables are embedded at BUILD time. If you changed .env, you must REBUILD the application for changes to take effect.');
  }

  const result = {
    clientId: '',
    hasClientId: false,
    isConfigured: false,
    errors: []
  };

  if (clientId) {
    result.clientId = clientId.trim();
    result.hasClientId = true;
  }

  result.isConfigured = result.hasClientId;

  // Always log Drive config status to diagnose missing buttons in production
  console.log('[EnvConfig] Google Drive config status:', {
    hasReactAppClientIdVar: Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID),
    hasLegacyClientIdVar: Boolean(process.env.GOOGLE_CLIENT_ID),
    hasClientId: result.hasClientId,
    isConfigured: result.isConfigured,
    clientIdPreview: result.clientId ? `${result.clientId.slice(0, 5)}...` : '(empty)'
  });

  if (process.env.NODE_ENV === 'development') {
    console.info('[EnvConfig] Google Drive environment probe', {
      hasReactAppClientIdVar: Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID),
      hasLegacyClientIdVar: Boolean(process.env.GOOGLE_CLIENT_ID),
      resolved: {
        hasClientId: result.hasClientId,
        isConfigured: result.isConfigured,
        clientIdPreview: result.clientId ? `${result.clientId.slice(0, 24)}...` : '',
        errors: result.errors
      }
    });
  }

  return result;
};

/**
 * Gets the source of configuration (environment or manual)
 * @param {string} endpoint - Current endpoint value
 * @param {string} token - Current token value
 * @returns {Object} - Source information for each field
 */
export const getConfigurationSource = (endpoint, token) => {
  const envConfig = loadRemotePackageApiConfig();
  
  return {
    endpoint: {
      fromEnvironment: envConfig.hasEndpoint && envConfig.endpoint === endpoint,
      isManaged: envConfig.hasEndpoint
    },
    token: {
      fromEnvironment: envConfig.hasToken && envConfig.token === token,
      isManaged: envConfig.hasToken
    }
  };
};

const envConfig = {
  loadRemotePackageApiConfig,
  loadGoogleDriveConfig,
  getConfigurationSource,
  isValidUrl
};

export default envConfig;
